import { log } from '@/log'
import { ApiClientWithAuth } from '@/services/api/client'
import { ApiService } from '@/services/api/service'
import { callWithAuth } from '@/services/auth/call-with-auth'
import * as Parallel from 'async-parallel'
import { Either, Left, Right } from 'fp-ts/lib/Either'
import _ from 'lodash'
import { Response } from 'superagent'
import { ApiClient } from './client'
import { Estimate } from './purchase'

const namespace: string = 'orderHistory'

export interface OrderHistory {
  id: string
  shortid: string
  item: SalesOrderItem[]
  tax: number
  totalPrice: number
  charge_sheet: Estimate
}

export interface SalesOrderItem {
  id: string
  /**
   * EngineのDBスキーマとしては必須ではないことに注意
   * @see https://github.com/Everforth/growthpalette-front/pull/7928#discussion_r1530563109
   */
  cart_id?: string
}

/**
 * TODO: FIXME: remove "any"
 */
export interface OrderHistoryJoinedWithProductclass {
  id: string
  skuId: string
  sku: any
  fkuId: string
  fku: any
  root: any
}

export class OrderHistoryApiService extends ApiService {
  async list(query?: any): Promise<Either<Error, Response>> {
    try {
      /**
       * 注文のステータスの変更が即座に取得できるように一律no-cacheで取得するようにした。
       * Engineの負荷が問題となる場合は切り替えられるように修正すること。
       */
      const response = await callWithAuth(
        () => ApiClient.get(`/api/user/order`)
          .set({
            'Cache-Control': 'no-cache',
          })
          .query(query)
      )

      log.debug({ service: `${namespace}/order`, response })

      return new Right(response)
    } catch (error) {
      return new Left(error)
    }
  }

  async get(
    salesOrderId: string
  ): Promise<Either<Error, Response>> {
    try {
      /**
       * 注文のステータスの変更が即座に取得できるように一律no-cacheで取得するようにした。
       * Engineの負荷が問題となる場合は切り替えられるように修正すること。
       */
      const response = await callWithAuth(
        () => ApiClient.get(`/api/user/order/${salesOrderId}`)
          .set({
            'Cache-Control': 'no-cache',
          })
      )

      log.debug({
        service: `${namespace}/commerce_sales_order/${salesOrderId}`,
        response,
      })

      return new Right(response)
    } catch (error) {
      return new Left(error)
    }
  }

  // TODO: Throttle simultaneous API requests number
  // TODO: Refactoring
  async join(
    items: any[]
  ): Promise<OrderHistoryJoinedWithProductclass[]> {
    const list: any[] = _.map(items, (item) => {
      return {
        id: item.id,
        skuId: item.product_id,
        fkuId: item.product_class_id,
      }
    })

    // Get Products
    const skuList: any[] = _.map(list, (entry) => {
      return {
        id: entry.id,
        sku: items,
      }
    })

    // Get FKU
    const fkuList: any[] = await Parallel.map(list, async (entry) => {

      try {
        const response = await ApiClientWithAuth
          .get(`/api/user-proxy/${this.appId}/product2/${entry.skuId}`, {
            with: 'whole,belonging_to',
          })

        const data = response.body.data

        const rootInfo = data.whole.belonging_to[0]

        // TODO: Check FKU tag
        return {
          id: entry.id,
          sku: data,
          fku: data.whole,
          root: rootInfo,
        }

      } catch (error) {
        log.debug(error)
      }

    })

    return _.merge(list, skuList, fkuList)
  }

  async cancel(id: string, code: CancelCode, note: string, noteOption?: string)
  : Promise<Either<Error, Response>> {
    try {
      const response = await callWithAuth(() =>
        ApiClient
          .post(`/api/user/order/${id}/cancel`)
          .send({
            cancel_code: code,
            note,
            ...((noteOption && code === CancelCode.OTHER) &&  { note_option: noteOption }),
          }))

      log.debug({ service: `${namespace}/cancel`, response })

      return new Right(response)
    } catch (error) {
      return new Left(error)
    }
  }

  checkCancelCodeRequiresNoteOption(code: CancelCode): boolean {
    return code === CancelCode.OTHER
  }
}

export enum CancelCode {
  DUPLICATE = 'duplicate',
  RETRY = 'retry',
  OPERATION_ERROR = 'operation-error',
  ORDER_MISTAKEN = 'order-mistaken',
  OTHER = 'other',
}
